import { motion } from "framer-motion";

const chapters = [
  { no: "01", title: "Strategy first", text: "We start with the plan. Before a single word goes out, we map the landscape — the stakeholders, the risks, and the narrative already forming." },
  { no: "02", title: "Discretion by default", text: "The most important work is often the least visible. We work quietly alongside leadership teams and their lawyers, protecting trust at every step." },
  { no: "03", title: "Coverage that lasts", text: "We build real relationships with the reporters who shape perception, so the story that runs is accurate, fair and durable." },
];

const fade = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 } }),
};

export const About = () => {
  return (
    <section id="about" data-testid="about-section" className="relative bg-navy text-ivory py-24 sm:py-32 grain overflow-hidden">
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <p className="overline text-gold mb-6">The firm</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-8 max-w-2xl">
              A boutique built for the moments that can&apos;t be undone.
            </h2>
            <p className="text-ivory/70 text-lg leading-relaxed max-w-2xl mb-14">
              Park Communications is a strategic communications and crisis management firm. We work with a select
              roster of clients — corporate leaders, brands, public figures and institutions — through scrutiny,
              transitions and reputational risk, and we help them build the credibility that lasts well beyond the
              headline.
            </p>

            <div className="space-y-px">
              {chapters.map((c, i) => (
                <motion.div
                  key={c.no}
                  custom={i}
                  variants={fade}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  data-testid={`manifesto-${c.no}`}
                  className="grid grid-cols-[3rem_1fr] gap-6 border-t border-white/12 py-8"
                >
                  <span className="font-serif text-xl text-gold">{c.no}</span>
                  <div>
                    <h3 className="font-serif text-2xl mb-2">{c.title}</h3>
                    <p className="text-ivory/60 leading-relaxed max-w-lg">{c.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758691736424-4b4273948341?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200"
                alt="Park Communications team in a client strategy meeting"
                className="w-full aspect-[3/4] object-cover photo-mono"
                loading="lazy"
              />
              <div className="absolute -bottom-px -left-px bg-navy border-t border-r border-gold/40 px-6 py-5">
                <p className="overline text-gold">Business &middot; Culture &middot; Public affairs</p>
              </div>
            </div>
            <p className="text-ivory/55 text-sm leading-relaxed mt-8">
              Placements secured across the nation&apos;s most influential newsrooms — in business, culture,
              entertainment, sports and public affairs.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
